import { useQuery } from '@tanstack/react-query';

import { Button } from '../../cdl/Button/Button';
import { Modal } from '../../cdl/Modal/Modal';
import { StatePlaceholder } from '../../cdl/StatePlaceholder/StatePlaceholder';
import { Text } from '../../cdl/Text/Text';
import { queries } from '../../common/api/queryKeys/queries';
import { translate } from '../../common/helpers/translate.helper';
import { IconAlertTriangle } from '../../common/icons/cdl/AlertTriangle';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../common/ui/Box';
import { useSupplierSubscriptionPlanCatalog } from '../useSupplierSubscriptionPlanCatalog';

import { SupplierSubscriptionPlanComparison } from './SupplierSubscriptionPlanComparison';

interface SupplierSubscriptionPlansModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    supplierGroupId: string;
}

export const SupplierSubscriptionPlansModal = ({
    isOpen,
    onDismiss,
    supplierGroupId,
}: SupplierSubscriptionPlansModalProps) => {
    const catalog = useSupplierSubscriptionPlanCatalog(isOpen, false);
    const subscriptionPlan = useQuery({
        ...queries.supplierGroups.detail(supplierGroupId)._ctx.subscriptionPlan,
        enabled: isOpen,
        throwOnError: false,
    });
    const isError = catalog.isError || subscriptionPlan.isError;
    const isFetching = catalog.isFetching || subscriptionPlan.isFetching;
    const isPending = catalog.isPending || subscriptionPlan.isPending;

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss} width={960}>
            <Box display="flex" flexDirection="column" gap={24} padding={24}>
                <Text as="h2" variant="title" paddingRight={40}>
                    {translate('supplierSubscriptionPlans.title')}
                </Text>
                {isError ? (
                    <Box display="flex" justifyContent="center" paddingY={32}>
                        <StatePlaceholder
                            title={translate('global.genericError.title')}
                            description={translate('global.genericError')}
                            Icon={IconAlertTriangle}
                            action={
                                <Button
                                    isLoading={isFetching}
                                    onClick={async () => {
                                        await Promise.all([catalog.refetch(), subscriptionPlan.refetch()]);
                                    }}
                                >
                                    {translate('closelinkInsights.tryAgain')}
                                </Button>
                            }
                        />
                    </Box>
                ) : null}
                {!isError && isPending ? <LoadingIndicator /> : null}
                {!isError && catalog.data && subscriptionPlan.data ? (
                    <SupplierSubscriptionPlanComparison
                        plans={catalog.data.plans}
                        currentPlan={subscriptionPlan.data.plan}
                    />
                ) : null}
            </Box>
        </Modal>
    );
};
